import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useI18n } from "../i18n/I18nContext";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { Calendar, Trophy, ExternalLink, Check, ArrowRight, Mail, Flag, Laptop, GraduationCap } from "lucide-react";
import usePageMeta from "../lib/usePageMeta";
import GoogleSignInButton from "../components/GoogleSignInButton";

const COAT = "https://customer-assets.emergentagent.com/job_66cda0d4-d07a-4a92-8bdd-b78d3aba21d7/artifacts/xbhowqoo_ephigie-hayti.jpg";
const SUPPORT_EMAIL = "email@seayisyen.net";

export default function Competition() {
  const { t } = useI18n();
  const { user } = useAuth();
  usePageMeta({ title: t("meta.competitionTitle"), description: t("meta.competitionDesc"), path: "/competition" });
  const [phase, setPhase] = useState("registration");
  const [competitors, setCompetitors] = useState([]);
  const [votedIds, setVotedIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("comp2026_voted") || "[]")); }
    catch { return new Set(); }
  });
  const [form, setForm] = useState({ name: "", email: "", phone: "", website_url: "" });
  const [submitting, setSubmitting] = useState(false);

  // Auto-fill registration form when a Google-signed-in user lands on this page,
  // without overwriting anything the user has already typed manually.
  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      name: f.name || user.name || "",
      email: f.email || user.email || "",
      phone: f.phone || user.phone || "",
    }));
  }, [user]);

  const loadCompetitors = async () => {
    try {
      const r = await api.get("/competition/competitors");
      setPhase(r.data.phase);
      setCompetitors(r.data.competitors || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    loadCompetitors();
    const id = setInterval(loadCompetitors, 30000);
    return () => clearInterval(id);
  }, []);

  const sortedCompetitors = useMemo(() => {
    return [...competitors].sort((a, b) => (b.votes || 0) - (a.votes || 0));
  }, [competitors]);

  const onRegister = async (e) => {
    e.preventDefault();
    if (phase !== "registration") {
      toast.error(t("competition.form.closed"));
      return;
    }
    setSubmitting(true);
    const resetForm = () => setForm({ name: "", email: "", phone: "", website_url: "" });
    try {
      // Only treat 5xx as thrown errors — we'll inspect 4xx manually so a benign
      // duplicate-email reply never surfaces as a generic "Something went wrong".
      const res = await api.post("/competition/register", form, {
        validateStatus: (s) => s >= 200 && s < 500,
      });
      if (res.status >= 200 && res.status < 300) {
        toast.success(t("competition.form.success"));
        resetForm();
        loadCompetitors();
      } else if (res.status === 400 && typeof res.data?.detail === "string" && /already/i.test(res.data.detail)) {
        // Duplicate email — user already registered; treat as success so they stop worrying.
        toast.success(t("competition.form.alreadyRegistered"));
        resetForm();
      } else {
        toast.error(formatApiError(res.data?.detail) || `HTTP ${res.status}`);
      }
    } catch (err) {
      // Network/timeout/CORS — the request MAY have reached the server (SendGrid email
      // confirms that). Show a reassuring message instead of a scary red fallback.
      toast.success(t("competition.form.networkMaybeSent"));
      resetForm();
      loadCompetitors();
    } finally { setSubmitting(false); }
  };

  const onVote = async (id) => {
    if (phase !== "voting") {
      toast.error(phase === "registration" ? t("competition.gallery.opens") : t("competition.gallery.closed"));
      return;
    }
    if (votedIds.has(id)) return;
    try {
      await api.post(`/competition/vote/${id}`);
      const next = new Set(votedIds); next.add(id); setVotedIds(next);
      localStorage.setItem("comp2026_voted", JSON.stringify([...next]));
      toast.success(t("competition.gallery.voted"));
      loadCompetitors();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="competition-page">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#141414]">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-[#0a0000] to-[#110000]" />
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(rgba(212,175,55,0.5) 1px, transparent 1px)", backgroundSize: "22px 22px" }}
        />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="lg:col-span-8"
          >
            <div className="overline mb-5" data-testid="competition-overline">{t("competition.overline")}</div>
            <div className="font-condensed tracking-[0.22em] uppercase text-[12px] text-[#d4af37] mb-5" data-testid="competition-tagline">
              {t("competition.tagline")}
            </div>
            <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight" data-testid="competition-title">
              {t("competition.title")}
            </h1>
            <p className="mt-6 max-w-2xl text-neutral-300 text-base md:text-lg leading-relaxed">
              {t("competition.subtitle")}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <PhaseBadge phase={phase} t={t} />
              <a
                href="#register"
                data-testid="competition-register-anchor"
                className="btn-imperial"
              >
                {t("competition.cta")} <ArrowRight size={16} />
              </a>
              <a
                href={`mailto:${SUPPORT_EMAIL}?subject=Seayisyen%20Competition%202026`}
                data-testid="competition-mail-cta"
                className="btn-outline-gold"
              >
                <Mail size={14} /> {SUPPORT_EMAIL}
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-4 flex justify-center"
          >
            <div className="relative">
              <div className="absolute -inset-8 bg-[#e6192b]/15 blur-3xl rounded-full" />
              <div className="absolute -inset-14 bg-[#d4af37]/10 blur-3xl rounded-full" />
              <div className="relative w-56 h-56 md:w-72 md:h-72 flex items-center justify-center border border-[#d4af37]/30 bg-[#050505]">
                <Trophy size={96} className="text-[#d4af37]" strokeWidth={1.2} />
                <img src={COAT} alt="" className="absolute -bottom-6 -right-6 h-20 w-20 object-contain mix-blend-screen opacity-80" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="py-20 md:py-24 border-b border-[#141414]">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-10">
            <div className="overline mb-3">{t("competition.timeline.overline")}</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">{t("competition.timeline.title")}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5" data-testid="competition-timeline">
            <TimelineCard
              accent="gold" icon={<Calendar size={18} />}
              label={t("competition.timeline.deadline.label")}
              value={t("competition.timeline.deadline.value")}
              active={phase === "registration"}
              testId="timeline-deadline"
            />
            <TimelineCard
              accent="red" icon={<Flag size={18} />}
              label={t("competition.timeline.voting.label")}
              value={t("competition.timeline.voting.value")}
              active={phase === "voting"}
              testId="timeline-voting"
            />
            <TimelineCard
              accent="gold" icon={<Trophy size={18} />}
              label={t("competition.timeline.final.label")}
              value={t("competition.timeline.final.value")}
              active={phase === "final"}
              testId="timeline-final"
            />
          </div>
        </div>
      </section>

      {/* PRIZES */}
      <section className="py-20 md:py-24 border-b border-[#141414] relative overflow-hidden" data-testid="competition-prizes">
        <div className="absolute -top-40 -right-40 h-96 w-96 bg-[#d4af37]/10 blur-3xl rounded-full pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 h-96 w-96 bg-[#e6192b]/10 blur-3xl rounded-full pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-10">
            <div className="overline mb-3">{t("competition.prizes.overline")}</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">{t("competition.prizes.title")}</h2>
            <p className="mt-3 text-neutral-400">{t("competition.prizes.subtitle")}</p>
          </div>

          <div className="card-imperial p-6 md:p-10 gold-shimmer-border border-[#d4af37]/40">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 bg-[#d4af37]/10 border border-[#d4af37]/50 flex items-center justify-center">
                  <Trophy size={26} className="text-[#d4af37]" />
                </div>
                <div>
                  <div className="overline">{t("competition.prizes.winnerLabel")}</div>
                  <div className="font-serif text-2xl md:text-3xl mt-0.5">{t("competition.prizes.title")}</div>
                </div>
              </div>
              <div className="inline-flex items-center gap-2 bg-[#e6192b] text-white px-4 py-2 font-condensed tracking-widest uppercase text-xs self-start md:self-auto">
                <Sparkle /> {t("competition.prizes.winnerValue")}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <PrizeOption
                icon={<Laptop size={22} />}
                label={t("competition.prizes.options")[0].label}
                title={t("competition.prizes.options")[0].title}
                desc={t("competition.prizes.options")[0].desc}
                testId="prize-option-a"
              />
              <PrizeOption
                icon={<GraduationCap size={22} />}
                label={t("competition.prizes.options")[1].label}
                title={t("competition.prizes.options")[1].title}
                desc={t("competition.prizes.options")[1].desc}
                testId="prize-option-b"
              />
            </div>
            <div className="mt-6 text-xs text-neutral-500 flex items-start gap-2">
              <span className="text-[#d4af37]">✦</span> {t("competition.prizes.footnote")}
            </div>
          </div>
        </div>
      </section>

      {/* Quick callouts (eligibility / votes / winner) — surfaced from the i18n library */}
      <CompetitionCallouts t={t} />

      {/* RULES */}
      <section className="py-20 md:py-24 border-b border-[#141414]">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="overline mb-3">{t("competition.rules.overline")}</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">{t("competition.rules.title")}</h2>
          </div>
          <div className="lg:col-span-8">
            <ol className="space-y-3" data-testid="competition-rules">
              {t("competition.rules.items").map((rule, i) => (
                <li
                  key={i}
                  className="card-imperial p-5 flex items-start gap-4"
                  data-testid={`competition-rule-${i + 1}`}
                >
                  <div className="font-serif text-3xl text-[#d4af37]/70 leading-none min-w-[2.5rem]">{String(i + 1).padStart(2, "0")}</div>
                  <div className="text-sm text-neutral-300 leading-relaxed">{rule}</div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* REGISTRATION FORM */}
      <section id="register" className="py-20 md:py-24 border-b border-[#141414]">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-10">
            <div className="overline mb-3">{t("competition.form.overline")}</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">{t("competition.form.title")}</h2>
          </div>
          {phase !== "registration" ? (
            <div className="card-imperial p-8 text-center" data-testid="competition-form-closed">
              <div className="font-serif text-2xl text-[#d4af37]">{t("competition.form.closed")}</div>
              <p className="mt-3 text-sm text-neutral-400">{t("competition.form.emailHint")}</p>
            </div>
          ) : (
            <form onSubmit={onRegister} className="card-imperial p-6 md:p-10 space-y-5" data-testid="competition-form">
              {!user && (
                <div className="space-y-4" data-testid="competition-google-block">
                  <GoogleSignInButton
                    next={"/competition-2026#register"}
                    testId="competition-google-btn"
                  />
                  <div className="flex items-center gap-3 text-[10px] text-neutral-500 uppercase tracking-[0.2em]">
                    <div className="flex-1 h-px bg-[#1a1a1a]" />
                    <span>{t("auth.orDivider", "or")}</span>
                    <div className="flex-1 h-px bg-[#1a1a1a]" />
                  </div>
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-5">
                <Field label={t("competition.form.name")} testId="comp-field-name"
                  value={form.name} onChange={(v) => setForm({ ...form, name: v })} required
                />
                <Field label={t("competition.form.email")} type="email" testId="comp-field-email"
                  value={form.email} onChange={(v) => setForm({ ...form, email: v })} required
                />
                <Field label={t("competition.form.phone")} testId="comp-field-phone"
                  value={form.phone} onChange={(v) => setForm({ ...form, phone: v })}
                />
                <Field label={t("competition.form.website")} type="url" testId="comp-field-website"
                  placeholder="https://"
                  hint={t("competition.form.websiteHint")}
                  value={form.website_url} onChange={(v) => setForm({ ...form, website_url: v })}
                />
              </div>
              <div className="text-xs text-neutral-500 flex items-center gap-2">
                <Mail size={12} className="text-[#d4af37]" /> {t("competition.form.emailHint")}
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="competition-submit"
                className="btn-imperial w-full md:w-auto"
              >
                {submitting ? t("competition.form.submitting") : t("competition.form.submit")} <ArrowRight size={16} />
              </button>
            </form>
          )}
        </div>
      </section>

      {/* GALLERY / VOTING */}
      <section className="py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-10">
            <div className="overline mb-3">{t("competition.gallery.overline")}</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">{t("competition.gallery.title")}</h2>
            <p className="mt-3 text-sm text-neutral-400">
              {phase === "registration" && t("competition.gallery.opens")}
              {phase === "voting" && null}
              {(phase === "final" || phase === "closed") && t("competition.gallery.closed")}
            </p>
          </div>

          {sortedCompetitors.length === 0 ? (
            <div className="card-imperial p-10 text-center text-neutral-500" data-testid="competition-gallery-empty">
              {t("competition.gallery.empty")}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="competition-gallery">
              {sortedCompetitors.map((c, idx) => {
                const voted = votedIds.has(c.id);
                const rank = idx + 1;
                return (
                  <motion.div
                    key={c.id}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: idx * 0.03 }}
                    className="card-imperial p-5 flex flex-col"
                    data-testid={`competition-card-${c.id}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="overline">#{String(rank).padStart(2, "0")}</div>
                      {phase === "voting" && (
                        <div className="text-[11px] font-condensed tracking-widest text-[#d4af37] uppercase">{c.votes} {t("competition.gallery.votesLabel")}</div>
                      )}
                      {(phase === "final" || phase === "closed") && (
                        <div className="text-[11px] font-condensed tracking-widest text-[#d4af37] uppercase">{c.votes} {t("competition.gallery.votesLabel")}</div>
                      )}
                    </div>
                    <div className="font-serif text-xl mb-1 leading-tight">{c.name}</div>
                    <a
                      href={c.website_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`competition-visit-${c.id}`}
                      className="text-xs text-[#d4af37]/80 hover:text-[#d4af37] transition-colors break-all inline-flex items-center gap-1"
                    >
                      <ExternalLink size={11} /> {c.website_url}
                    </a>
                    <div className="mt-auto pt-5">
                      {phase === "voting" ? (
                        <button
                          onClick={() => onVote(c.id)}
                          disabled={voted}
                          data-testid={`competition-vote-${c.id}`}
                          className={voted ? "btn-outline-gold w-full opacity-60 cursor-not-allowed" : "btn-imperial w-full"}
                        >
                          {voted ? (<><Check size={14} /> {t("competition.gallery.voted")}</>) : (<>{t("competition.gallery.voteBtn")} <ArrowRight size={14} /></>)}
                        </button>
                      ) : (
                        <a
                          href={c.website_url}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`competition-visit-btn-${c.id}`}
                          className="btn-outline-gold w-full"
                        >
                          {t("competition.gallery.visit")}
                        </a>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function PhaseBadge({ phase, t }) {
  const color = phase === "voting" ? "bg-[#e6192b] text-white" : phase === "registration" ? "bg-[#d4af37] text-black" : "bg-[#1a1a1a] text-neutral-400 border border-[#2a2a2a]";
  return (
    <div data-testid={`competition-phase-${phase}`} className={`inline-flex items-center gap-2 px-3 py-1.5 text-[11px] font-condensed tracking-widest uppercase ${color}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-80" /> {t(`competition.phases.${phase}`)}
    </div>
  );
}

function TimelineCard({ icon, label, value, accent, active, testId }) {
  const ring = active ? (accent === "red" ? "border-[#e6192b]/70" : "border-[#d4af37]/70") : "border-[#1f1f1f]";
  const tint = accent === "red" ? "text-[#e6192b]" : "text-[#d4af37]";
  return (
    <div data-testid={testId} className={`card-imperial p-6 border ${ring} ${active ? "shadow-[0_0_40px_-20px_rgba(212,175,55,0.4)]" : ""}`}>
      <div className={`inline-flex items-center justify-center h-10 w-10 border border-[#1f1f1f] mb-4 ${tint}`}>{icon}</div>
      <div className="overline">{label}</div>
      <div className="font-serif text-xl md:text-2xl mt-1 leading-tight">{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder, testId, hint }) {
  return (
    <label className="block">
      <span className="overline block mb-1.5">{label}{required ? " *" : ""}</span>
      <input
        type={type}
        required={required}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-[#0a0a0a] border border-[#1f1f1f] focus:border-[#d4af37] outline-none px-4 py-3 text-sm text-neutral-100 placeholder-neutral-600 transition-colors"
      />
      {hint && <span className="block mt-1.5 text-[11px] text-neutral-500">{hint}</span>}
    </label>
  );
}

function PrizeOption({ icon, label, title, desc, testId }) {
  return (
    <div data-testid={testId} className="relative p-6 border border-[#1f1f1f] bg-[#0a0a0a] hover:border-[#d4af37]/60 transition-colors group">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-11 w-11 bg-[#050505] border border-[#2a2a2a] flex items-center justify-center text-[#d4af37] group-hover:border-[#d4af37] transition-colors">{icon}</div>
        <div className="overline">{label}</div>
      </div>
      <div className="font-serif text-xl md:text-2xl leading-tight">{title}</div>
      <p className="mt-3 text-sm text-neutral-400 leading-relaxed">{desc}</p>
    </div>
  );
}

function Sparkle() {
  return <span className="inline-block h-1.5 w-1.5 bg-white rounded-full" />;
}

function CompetitionCallouts({ t }) {
  const items = [
    { key: "competition.eligibility", icon: "👥" },
    { key: "competition.votes", icon: "🗳" },
    { key: "competition.winner_announcement", icon: "🏆" },
  ];
  const visible = items
    .map((it) => ({ ...it, text: t(it.key, "") }))
    .filter((it) => it.text && it.text !== it.key);
  if (visible.length === 0) return null;
  return (
    <section className="py-10 md:py-14 border-b border-[#141414]" data-testid="competition-callouts">
      <div className="max-w-7xl mx-auto px-5 md:px-10 grid sm:grid-cols-3 gap-4">
        {visible.map(({ key, text }, i) => (
          <div
            key={key}
            data-testid={`callout-${key.split(".").pop()}`}
            className="card-imperial p-5 flex items-start gap-3"
          >
            <span className="font-serif text-2xl text-[#d4af37]/70 leading-none">{String(i + 1).padStart(2, "0")}</span>
            <span className="text-sm text-neutral-200 leading-relaxed">{text}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
